import { Injectable } from '@angular/core';
import { TokenCount } from '../templateGen/templateInterfaces';

// NUOVO: Interfaccia per descrivere un prezzo a scaglioni
export interface TieredPrice {
  threshold: number;          // La soglia di token (es. 200000)
  priceUpToThreshold: number; // Prezzo se i token sono <= della soglia
  priceOverThreshold: number; // Prezzo se i token sono > della soglia
}

// MODIFICATO: L'interfaccia ora accetta sia un numero (prezzo fisso) sia un prezzo a scaglioni
export interface ModelPricing {
  name: string;
  inputPrice: number | TieredPrice;  // Può essere un prezzo fisso o a scaglioni
  outputPrice: number | TieredPrice; // Può essere un prezzo fisso o a scaglioni
}

export interface TokenCostCalculation {
  inputCost: number;
  outputCost: number;
  totalCost: number;
}

@Injectable({
  providedIn: 'root'
})
export class ModelPricingService {
  // I nomi dei modelli ('Gemini 3', 'Gemini 2.5 pro', etc.) corrispondono a quelli definiti in StatoSessioneService (e a backend infer.py)
  private modelPricing: { [key: string]: ModelPricing } = {
    'Gemini 3': {
      name: 'Gemini 3 Pro Preview',
      inputPrice: {
        threshold: 200000,
        priceUpToThreshold: 2.00,
        priceOverThreshold: 4.00
      },
      outputPrice: {
        threshold: 200000,
        priceUpToThreshold: 12.00,
        priceOverThreshold: 18.00
      }
    },
    'Gemini 2.5 pro': {
      name: 'Gemini 2.5 Pro',
      inputPrice: {
        threshold: 200000,
        priceUpToThreshold: 1.25,
        priceOverThreshold: 2.50
      },
      outputPrice: {
        threshold: 200000,
        priceUpToThreshold: 10.00,
        priceOverThreshold: 15.00
      }
    },
    'Gemini 2.5 flash': {
      name: 'Gemini 2.5 Flash',
      inputPrice: 0.30,   // Prezzo fisso per text/image/video
      outputPrice: 2.50   // Prezzo fisso
    },
    'Claude Opus 4.6': {
      name: 'Claude Opus 4.6',
      inputPrice: 5.00,   // $5 per MTok input
      outputPrice: 25.00  // $25 per MTok output
    },
    'Claude Sonnet 4.6': {
      name: 'Claude Sonnet 4.6',
      inputPrice: 3.00,   // $3 per MTok input
      outputPrice: 15.00  // $15 per MTok output
    },
    'Claude Haiku 4.5': {
      name: 'Claude Haiku 4.5',
      inputPrice: 1.00,   // $1 per MTok input
      outputPrice: 5.00   // $5 per MTok output
    }
  };

  getModelPricing(modelName: string): ModelPricing | undefined {
    return this.modelPricing[modelName];
  }

  // MODIFICATO: La funzione ora gestisce sia i prezzi fissi sia quelli a scaglioni.
  calculateCost(modelName: string, tokenCount: TokenCount): TokenCostCalculation {
    const pricing = this.getModelPricing(modelName);

    if (!pricing) {
      return { inputCost: 0, outputCost: 0, totalCost: 0 };
    }

    let finalInputPrice: number;
    let finalOutputPrice: number;

    // --- Logica per il prezzo di INPUT ---
    if (typeof pricing.inputPrice === 'number') {
      // Caso semplice: prezzo fisso
      finalInputPrice = pricing.inputPrice;
    } else {
      // Caso complesso: prezzo a scaglioni
      const tieredInput = pricing.inputPrice as TieredPrice;
      finalInputPrice = tokenCount.prompt_tokens <= tieredInput.threshold
        ? tieredInput.priceUpToThreshold
        : tieredInput.priceOverThreshold;
    }

    // --- Logica per il prezzo di OUTPUT ---
    // NOTA: La documentazione dice che il prezzo di output dipende dalla dimensione del PROMPT
    if (typeof pricing.outputPrice === 'number') {
      // Caso semplice: prezzo fisso
      finalOutputPrice = pricing.outputPrice;
    } else {
      // Caso complesso: prezzo a scaglioni basato sui token del PROMPT
      const tieredOutput = pricing.outputPrice as TieredPrice;
      finalOutputPrice = tokenCount.prompt_tokens <= tieredOutput.threshold
        ? tieredOutput.priceUpToThreshold
        : tieredOutput.priceOverThreshold;
    }

    // Calcolo finale (i prezzi sono per 1 milione di token)
    const inputCost = (tokenCount.prompt_tokens / 1_000_000) * finalInputPrice;
    const outputCost = (tokenCount.completion_tokens / 1_000_000) * finalOutputPrice;
    const totalCost = inputCost + outputCost;

    return {
      inputCost,
      outputCost,
      totalCost
    };
  }
}
